import axios, { CancelTokenSource } from "axios";
import { ActionTree } from "vuex";
import { ClientProject } from "@/models/ClientProject";
import { Lead, ClientEditViewModel, LeadEditViewModel, LeadsPageViewModel, RefreshGridMode, PhonePopupData } from "@/models/leads/Lead";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { AdTitlesByLeads, Assignee, ChannelMultiselectItem, MultiselectItem, SelectListItem } from "@/models/MultiselectItem";
import { TableSettings } from "@/models/TableSettings";
import { LeadStatistic, LeadStatisticExtended, PhoneLeadStats } from "@/models/leads/LeadStatistic";
import { LeadStatus } from "@/models/leads/LeadStatus";
import { LeadsState } from "@/models/states/LeadsState";
import { RootState } from "@/models/states/RootState";
import ConfigService from "@/services/ConfigService";
import ApiService, { AssigneesForClientPayload, CommonMultiselectPayload, LeadsPayload, LeadsStatisticPhonesPayload } from "@/services/api/ApiService";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";

const actions: ActionTree<LeadsState, RootState> = {
  clearAdditionalStatuses({ commit }): void {
    commit("setAdditionalStatuses", []);
  },

  clearCommitments({ commit }): void {
    commit("setCommitments", []);
  },

  clearProcesses({ commit }): void {
    commit("setProcesses", []);
  },

  getAdGroups({ commit }, payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getAdGroups(payload)
        .then((data: MultiselectItem[]) => {
          commit("setAdGroups", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdTitles({ commit }, payload: CommonMultiselectPayload): Promise<AdTitlesByLeads[]> {
    return new Promise((resolve, reject) => {
      ApiService.getAdTitles(payload)
        .then((data: AdTitlesByLeads[]) => {
          commit("setAdTitles", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdditionalStatuses({ commit }, payload: CommonMultiselectPayload): Promise<SelectListItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getAdditionalStatuses(payload)
        .then((data: SelectListItem[]) => {
          commit("setAdditionalStatuses", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssets({ commit }, payload: CommonMultiselectPayload): Promise<ClientProject[]> {
    return new Promise((resolve, reject) => {
      ApiService.getAssets(payload)
        .then((data: ClientProject[]) => {
          commit("setAssets", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssignees({ commit }, payload: CommonMultiselectPayload): Promise<Assignee[]> {
    return new Promise((resolve, reject) => {
      ApiService.getAssignees(payload)
        .then((data: Assignee[]) => {
          commit("setAssignees", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssigneesForClient({ commit }, payload: AssigneesForClientPayload): Promise<Assignee[]> {
    return new Promise((resolve, reject) => {
      ApiService.getAssigneesForClient(payload)
        .then((data: Assignee[]) => {
          commit("setAssignees", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getChannels({ commit }, payload: CommonMultiselectPayload): Promise<ChannelMultiselectItem[]> {
    commit("setChannels", []);
    return new Promise((resolve, reject) => {
      ApiService.getChannels(payload)
        .then((data: ChannelMultiselectItem[]) => {
          commit("setChannels", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCompanies({ commit }): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getCompanies()
        .then((data: MultiselectItem[]) => {
          commit("setCompanies", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCommitments({ commit }, payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getProjects(payload)
        .then((data: MultiselectItem[]) => {
          commit("setCommitments", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getProcesses({ commit }, companyId: number): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getProcesses(companyId)
        .then((data: MultiselectItem[]) => {
          commit("setProcesses", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getClientEditViewModel({ commit }, id: number): Promise<ClientEditViewModel> {
    commit("setClientEditViewModel", null);
    return new Promise((resolve, reject) => {
      ApiService.getClientEditViewModel(id)
        .then((data: ClientEditViewModel) => {
          commit("setClientEditViewModel", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getLeadEditViewModel({ commit }, id: number): Promise<LeadEditViewModel> {
    commit("setLeadEditViewModel", null);
    return new Promise((resolve, reject) => {
      ApiService.getLeadEditViewModel(id)
        .then((data: LeadEditViewModel) => {
          commit("setLeadEditViewModel", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getLeads({ commit, state }, payload: LeadsPayload): Promise<DataSourceResult> {
    const updateStore: boolean = payload.pageSize !== ConfigService.maxIntValue;
    if (updateStore) {
      commit("setLeadsLoading", true);
    }
    return new Promise((resolve, reject) => {
      const axiosSource: CancelTokenSource = axios.CancelToken.source();
      commit("setLeadsCancelTokenSource", axiosSource);
      ApiService.getLeads(payload, axiosSource)
        .then((data: DataSourceResult) => {
          if (updateStore) {
            if (state.page > 1) {
              data.Data = state.leads.Data?.concat(data.Data);
            }
            commit("setLeads", data);
            commit("setLeadsLoading", false);
            commit("setLeadsCancelTokenSource", null);
          }
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getLeadsPageData({ commit }): Promise<LeadsPageViewModel> {
    commit("setLeadsPageData", null);
    return new Promise((resolve, reject) => {
      ApiService.getLeadsPageData()
        .then((data: LeadsPageViewModel) => {
          commit("setLeadsPageData", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
    },

    checkPhonePopup({ commit }): Promise<PhonePopupData> {
        commit("setPhonePopupData", { showPopup: false, message: "" });
        return new Promise((resolve, reject) => {
            ApiService.checkPhonePopup()
                .then((data: PhonePopupData) => {
                    commit("setPhonePopupData", data);
                    resolve(data);
                })
                .catch((error) => {
                    console.error("Error in pop-up:", error);
                    reject(error);
                });
        });
    },

  

  getLeadsStatisticPhones({ commit }, payload: LeadsStatisticPhonesPayload): Promise<PhoneLeadStats> {
    commit("setLeadsStatisticPhones", null);
    return new Promise((resolve, reject) => {
      ApiService.getLeadsStatisticPhones(payload)
        .then((data: PhoneLeadStats) => {
          commit("setLeadsStatisticPhones", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getTags({ commit }, payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    commit("setTags", []);
    return new Promise((resolve, reject) => {
      ApiService.getTags(payload)
        .then((data: MultiselectItem[]) => {
          commit("setTags", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  logout({ commit }): void {
    commit("logout");
  },

  resetState({ commit }): void {
    commit("resetState");
  },

  setAssets({ commit }, assets: ClientProject[]): void {
    commit("setAssets", assets);
  },

  setAssignees({ commit }, assignees: Assignee[]): void {
    commit("setAssignees", assignees);
  },

  setColumns({ commit }, columns: GridColumn[]): void {
    commit("setColumns", columns);
  },

  setColumnsFilters({ commit }, columnsFilters: FilterCollection): void {
    commit("setColumnsFilters", columnsFilters);
  },

  setDatePeriod({ commit }, datePeriod: Date[]): void {
    commit("setDatePeriod", datePeriod);
  },

  setDeactivateStats({ commit }, deactivateStats: boolean): void {
    commit("setDeactivateStats", deactivateStats);
  },

  setGraphsHiddenOnScroll({ commit }, graphsHiddenOnScroll: boolean): void {
    commit("setGraphsHiddenOnScroll", graphsHiddenOnScroll);
  },

  setSelectedLeadId({ commit }, clientId: number): void {
    commit("setSelectedLeadId", clientId);
  },

  setClientEditViewModel({ commit }, clientEditViewModel: ClientEditViewModel): void {
    commit("setClientEditViewModel", clientEditViewModel);
  },

  setClientsDatePeriod({ commit }, datePeriod: Date[]): void {
    commit("setClientsDatePeriod", datePeriod);
  },

  setClientsSearchValue({ commit }, searchValue: string): void {
    commit("setClientsSearchValue", searchValue);
  },

  setClientsSelectedAdditionalStatusesIds({ commit }, selectedAdditionalStatusesIds: number[]): void {
    commit("setClientsSelectedAdditionalStatusesIds", selectedAdditionalStatusesIds);
  },

  setClientsSelectedAdGroupsIds({ commit }, selectedAdGroupsIds: string[]): void {
    commit("setClientsSelectedAdGroupsIds", selectedAdGroupsIds);
  },

  setClientsSelectedAdTitles({ commit }, selectedAdTitles: string[]): void {
    commit("setClientsSelectedAdTitles", selectedAdTitles);
  },

  setClientsSelectedAssetsIds({ commit }, selectedAssetsIds: string[]): void {
    commit("setClientsSelectedAssetsIds", selectedAssetsIds);
  },

  setClientsSelectedAssigneesIds({ commit }, selectedAssigneesIds: number[]): void {
    commit("setClientsSelectedAssigneesIds", selectedAssigneesIds);
  },

  setClientsSelectedCallStatusesIds({ commit }, selectedCallStatusesIds: MultiselectItem[]): void {
    commit("setClientsSelectedCallStatusesIds", selectedCallStatusesIds);
  },

  setClientsSelectedChannelMetadataTypesIds({ commit }, selectedChannelMetadataTypesIds: number[]): void {
    commit("setClientsSelectedChannelMetadataTypesIds", selectedChannelMetadataTypesIds);
  },

  setClientsSelectedChannelsIds({ commit }, selectedChannelsIds: number[]): void {
    commit("setClientsSelectedChannelsIds", selectedChannelsIds);
  },

  setClientsSelectedCompaniesIds({ commit }, selectedCompaniesIds: number[]): void {
    commit("setClientsSelectedCompaniesIds", selectedCompaniesIds);
  },

  setClientsSelectedCommitmentsIds({ commit }, selectedCommitmentsIds: number[]): void {
    commit("setClientsSelectedCommitmentsIds", selectedCommitmentsIds);
  },

  setClientsSelectedProcessesIds({ commit }, selectedProcessesIds: number[]): void {
    commit("setClientsSelectedProcessesIds", selectedProcessesIds);
  },

  setClientsSelectedFoundMetadataTypesIds({ commit }, selectedFoundMetadataTypesIds: number[]): void {
    commit("setClientsSelectedFoundMetadataTypesIds", selectedFoundMetadataTypesIds);
  },

  setClientsSelectedLeadStatusesIds({ commit }, selectedLeadStatusesIds: LeadStatus[]): void {
    commit("setClientsSelectedLeadStatusesIds", selectedLeadStatusesIds);
  },

  setClientsSelectedPriceType({ commit }, selectedPriceType: string): void {
    commit("setClientsSelectedPriceType", selectedPriceType);
  },

  setClientsSelectedSaleStatusesIds({ commit }, selectedSaleStatusesIds: LeadStatus[]): void {
    commit("setClientsSelectedSaleStatusesIds", selectedSaleStatusesIds);
  },

  setClientsSelectedTags({ commit }, selectedTags: string[]): void {
    commit("setClientsSelectedTags", selectedTags);
  },

  setLeadEditViewModel({ commit }, leadEditViewModel: LeadEditViewModel): void {
    commit("setLeadEditViewModel", leadEditViewModel);
  },
  
  setLeadsCancelTokenSource({ commit }, cancelTokenSource: CancelTokenSource) {
    commit("setLeadsCancelTokenSource", cancelTokenSource);
  },

  setStatsCancelTokenSource({ commit }, cancelTokenSource: CancelTokenSource) {
    commit("setStatsCancelTokenSource", cancelTokenSource);
  },

  setLeadsPayload({ commit }, leadsPayload: LeadsPayload): void {
    commit("setLeadsPayload", leadsPayload);
  },



  setLeadsStatisticByAdditionalStatus({ commit }, data: LeadStatistic[] | null): void {
    commit("setLeadsStatisticByAdditionalStatus", data);
  },

  setLeadsStatisticByCallStatus({ commit }, data: LeadStatistic[] | null): void {
    commit("setLeadsStatisticByCallStatus", data);
  },

  setLeadsStatisticByCampaigns({ commit, state }, data: LeadStatistic[] | null): void {
    commit("setLeadsStatisticByCampaigns", data);
  },

  setLeadsStatisticByPhoneAndForms({ commit }, data: LeadStatistic[] | null): void {
    commit("setLeadsStatisticByPhoneAndForms", data);
  },

  setLeadsStatisticBySaleStatus({ commit, state }, data: LeadStatistic[] | null): void {
    commit("setLeadsStatisticBySaleStatus", data);
  },

  setLeadsStatisticChannelMetadataType({ commit, state }, data: LeadStatistic[] | null): void {
    commit("setLeadsStatisticChannelMetadataType", data);
  },

  setLeadsStatisticDealMeetingsLeads({ commit, state }, data: LeadStatisticExtended[] | null): void {
    commit("setLeadsStatisticDealMeetingsLeads", data);
  },



  setLeadsStatisticPhones({ commit }, phoneLeadStats: PhoneLeadStats): void {
    commit("setLeadsStatisticPhones", phoneLeadStats);
  },

  setNeedToRefreshLeadsGrid({ commit }, needToRefreshLeadsGrid: RefreshGridMode): void {
    commit("setNeedToRefreshLeadsGrid", needToRefreshLeadsGrid);
  },

  setPage({ commit }, page: number): void {
    commit("setPage", page);
  },

  setSearchValue({ commit }, searchValue: string): void {
    commit("setSearchValue", searchValue);
  },

  setSelectedAdditionalStatusesIds({ commit }, selectedAdditionalStatusesIds: number[]): void {
    commit("setSelectedAdditionalStatusesIds", selectedAdditionalStatusesIds);
  },

  setSelectedAdGroupsIds({ commit }, selectedAdGroupsIds: string[]): void {
    commit("setSelectedAdGroupsIds", selectedAdGroupsIds);
  },

  setSelectedAdTitles({ commit }, selectedAdTitles: string[]): void {
    commit("setSelectedAdTitles", selectedAdTitles);
  },

  setSelectedAssetsIds({ commit }, selectedAssetsIds: string[]): void {
    commit("setSelectedAssetsIds", selectedAssetsIds);
  },

  setSelectedAssigneesIds({ commit }, selectedAssigneesIds: number[]): void {
    commit("setSelectedAssigneesIds", selectedAssigneesIds);
  },

  setSelectedCallStatusesIds({ commit }, selectedCallStatusesIds: MultiselectItem[]): void {
    commit("setSelectedCallStatusesIds", selectedCallStatusesIds);
  },

  setSelectedChannelMetadataTypesIds({ commit }, selectedChannelMetadataTypesIds: number[]): void {
    commit("setSelectedChannelMetadataTypesIds", selectedChannelMetadataTypesIds);
  },

  setSelectedChannelsIds({ commit }, selectedChannelsIds: number[]): void {
    commit("setSelectedChannelsIds", selectedChannelsIds);
  },

  setSelectedCompaniesIds({ commit }, selectedCompaniesIds: number[]): void {
    commit("setSelectedCompaniesIds", selectedCompaniesIds);
  },

  setSelectedCommitmentsIds({ commit }, selectedCommitmentsIds: number[]): void {
    commit("setSelectedCommitmentsIds", selectedCommitmentsIds);
  },

  setSelectedProcessesIds({ commit }, selectedProcessesIds: number[]): void {
    commit("setSelectedProcessesIds", selectedProcessesIds);
  },

  setSelectedFoundMetadataTypesIds({ commit }, selectedFoundMetadataTypesIds: number[]): void {
    commit("setSelectedFoundMetadataTypesIds", selectedFoundMetadataTypesIds);
  },

  setSelectedLead({ commit }, selectedLead?: Lead): void {
    commit("setSelectedLead", selectedLead);
  },

  setSelectedClient({ commit }, selectedClient?: Lead): void {
    commit("setSelectedClient", selectedClient);
  },

  setSelectedLeadGridColumnsIds({ commit }, selectedLeadGridColumnsIds: number[]): void {
    commit("setSelectedLeadGridColumnsIds", selectedLeadGridColumnsIds);
  },

  setSelectedLeadStatusesIds({ commit }, selectedLeadStatusesIds: LeadStatus[]): void {
    commit("setSelectedLeadStatusesIds", selectedLeadStatusesIds);
  },

  setSelectedPriceType({ commit }, selectedPriceType: string): void {
    commit("setSelectedPriceType", selectedPriceType);
  },

  setSelectedSaleStatusesIds({ commit }, selectedSaleStatusesIds: LeadStatus[]): void {
    commit("setSelectedSaleStatusesIds", selectedSaleStatusesIds);
  },

  setSelectedTags({ commit }, selectedTags: string[]): void {
    commit("setSelectedTags", selectedTags);
  },

  setShowDeleted({ commit }, showDeleted: boolean): void {
    commit("setShowDeleted", showDeleted);
  },

  setShowDeletedClientLeads({ commit }, showDeletedClientLeads: boolean): void {
    commit("setShowDeletedClientLeads", showDeletedClientLeads);
  },

  setShowFavourite({ commit }, showFavourite: boolean): void {
    commit("setShowFavourite", showFavourite);
  },

  setShowFavouriteClientLeads({ commit }, showFavouriteClientLeads: boolean): void {
    commit("setShowFavouriteClientLeads", showFavouriteClientLeads);
  },

  setShowGraphs({ commit }, showGraphs: boolean): void {
    commit("setShowGraphs", showGraphs);
  },

  setShowGraphsClientLeads({ commit }, showGraphsClientLeads: boolean): void {
    commit("setShowGraphsClientLeads", showGraphsClientLeads);
  },

  setShowNotActiveCampaign({ commit }, showNotActiveCampaign: boolean): void {
    commit("setShowNotActiveCampaign", showNotActiveCampaign);
  },

  setShowNotActiveCampaignClientLeads({ commit }, showNotActiveCampaignClientLeads: boolean): void {
    commit("setShowNotActiveCampaignClientLeads", showNotActiveCampaignClientLeads);
  },

  setSortDir({ commit }, sortDir: string): void {
    commit("setSortDir", sortDir);
  },

  setSortField({ commit }, sortField: string): void {
    commit("setSortField", sortField);
  },

  setUniqueByCampaign({ commit }, uniqueByCampaign: boolean): void {
    commit("setUniqueByCampaign", uniqueByCampaign);
  },

  setUniqueByCampaignClientLeads({ commit }, uniqueByCampaignClientLeads: boolean): void {
    commit("setUniqueByCampaignClientLeads", uniqueByCampaignClientLeads);
  },

  setTableSettings({ commit }, tableSettings: TableSettings): void {
    commit("setTableSettings", tableSettings);
  },

  setTags({ commit }, tags: MultiselectItem[]): void {
    commit("setTags", tags);
    },

    setHidePhonePopup({ commit }): Promise<void> {
        return new Promise((resolve, reject) => {
            ApiService.setHidePopupCookie()
                .then(() => {
                    commit("setHidePhonePopup", true);
                    resolve();
                })
                .catch((error) => {
                    console.error("Error setting hide popup cookie:", error);
                    reject(error);
                });
        });
    },

};
export default actions;
